export const MIN_VIDEO_DURATION_SECONDS = 4;
export const MAX_VIDEO_DURATION_SECONDS = 45;

const MIME_TYPES_BY_EXTENSION = {
  mp4: new Set(["video/mp4", "application/mp4"]),
  mov: new Set(["video/quicktime", "video/mov", "video/x-quicktime"]),
};

export const isSupportedVideoFormat = (file) => {
  if (!file) return false;

  const extension = (file.name || "").match(/\.([^.]+)$/)?.[1]?.toLowerCase();
  const allowedMimeTypes = MIME_TYPES_BY_EXTENSION[extension];
  const mimeType = (file.type || "").toLowerCase();

  // Some mobile browsers leave File.type blank, so retain the extension check
  // as a fallback while rejecting known MIME types that do not match the file.
  return (
    Boolean(allowedMimeTypes) &&
    (mimeType === "" || allowedMimeTypes.has(mimeType))
  );
};

export const isVideoDurationInRange = (duration) =>
  Number.isFinite(duration) &&
  duration >= MIN_VIDEO_DURATION_SECONDS &&
  duration <= MAX_VIDEO_DURATION_SECONDS;

export const readVideoDuration = (file) =>
  new Promise((resolve, reject) => {
    const video = document.createElement("video");
    const objectUrl = URL.createObjectURL(file);

    const cleanup = () => {
      video.onloadedmetadata = null;
      video.onerror = null;
      video.removeAttribute("src");
      URL.revokeObjectURL(objectUrl);
    };

    video.preload = "metadata";
    video.onloadedmetadata = () => {
      const { duration } = video;
      cleanup();
      resolve(duration);
    };
    video.onerror = () => {
      cleanup();
      reject(new Error("Unable to read video metadata"));
    };
    video.src = objectUrl;
  });

export const readVideoDimensions = (file) =>
  new Promise((resolve, reject) => {
    const video = document.createElement("video");
    const objectUrl = URL.createObjectURL(file);

    const cleanup = () => {
      video.onloadedmetadata = null;
      video.onerror = null;
      video.removeAttribute("src");
      URL.revokeObjectURL(objectUrl);
    };

    video.preload = "metadata";
    video.onloadedmetadata = () => {
      const dimensions = {
        width: video.videoWidth,
        height: video.videoHeight,
      };
      cleanup();
      resolve(dimensions);
    };
    video.onerror = () => {
      cleanup();
      reject(new Error("Unable to read video dimensions"));
    };
    video.src = objectUrl;
  });

export const getVideoOrientation = ({ width, height } = {}) => {
  if (
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return null;
  }

  if (width === height) return "square";
  return width > height ? "landscape" : "portrait";
};

const validateVideoDuration = async (
  file,
  durationReader = readVideoDuration,
) => {
  try {
    const duration = await durationReader(file);
    if (!Number.isFinite(duration)) {
      return { valid: false, error: "metadata" };
    }
    if (!isVideoDurationInRange(duration)) {
      return { valid: false, error: "duration" };
    }
  } catch {
    return { valid: false, error: "metadata" };
  }

  return { valid: true };
};

export const validateUploadedVideo = async (
  file,
  durationReader = readVideoDuration,
) => {
  if (!isSupportedVideoFormat(file)) {
    return { valid: false, error: "format" };
  }

  return validateVideoDuration(file, durationReader);
};

// Browser capture may produce MP4 or WebM depending on MediaRecorder support,
// so captured videos do not apply the upload file-format rule. Callers can
// additionally require the dimensions of the recorded file to match the test.
export const validateCapturedVideo = async (file, options = {}) => {
  // Retain the previous injected-duration-reader signature for callers/tests
  // that pass a function as the second argument.
  const normalizedOptions =
    typeof options === "function" ? { durationReader: options } : options;
  const {
    durationReader = readVideoDuration,
    dimensionsReader = readVideoDimensions,
    preferredOrientation,
  } = normalizedOptions || {};

  const durationResult = await validateVideoDuration(file, durationReader);
  if (!durationResult.valid || !preferredOrientation) return durationResult;

  try {
    const actualOrientation = getVideoOrientation(await dimensionsReader(file));
    if (!actualOrientation) return { valid: false, error: "metadata" };
    if (actualOrientation !== preferredOrientation) {
      return { valid: false, error: "orientation" };
    }
  } catch {
    return { valid: false, error: "metadata" };
  }

  return { valid: true };
};
